import React, { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { useParams, useHistory } from 'react-router-dom'
import ComplaintInternalMessages from './ComplaintInternalMessagesDetail'
import { AppState } from '../../../../reducers'
import { ComplaintDetailState } from '../../../../reducers/complaint/complaintDetail'
import { useTranslation } from 'react-i18next'
import { Paper, Box, Typography, Button, Table, TableCell, TableHead, TableRow, TableBody, CircularProgress, IconButton, Stack } from '@mui/material';
import { AssignmentIndOutlined, AttachFile, FlakyOutlined, HourglassBottomOutlined, KeyboardReturnOutlined } from '@mui/icons-material';
import moment from 'moment';
import { fetchComplaint } from '../../../../actions/complaint/complaintDetail'
import { fetchComplaintInternalMessages } from '../../../../actions/complaint/complaintInternalMessage'
import { ComplaintInternalMessageState } from '../../../../reducers/complaint/complaintInternalMessage'
import { SessionState, isClient, isHeadSales, isStaff } from '../../../../reducers/auth/session'
import { receiptComplaintTypeToDescription } from '../../../GeneralDesc'
import { ISTableHeaderCell, ISTableRow } from '../../../Utils/ISTable'
import ComplaintInternalMessage from '../../../../models/ComplaintInternalMessage'
import { upsertComplaintInternalMessage } from '../../../../actions/complaint/complaintInternalMessageUpsert'
import Complaint from '../../../../models/Complaint'
import { upsertComplaint } from '../../../../actions/complaint/complaintUpsert'
import { useThemeContext } from '../../../../theme/ThemeContext'
import { downloadComplaintInternalMessageFile } from '../../../../actions/complaint/complaintInternalMessageDownloadFile'
import { GenericModalDialog } from '../../../modalDialog/GenericModalDialog'
import Selector from '../../../Selector'
import { complaintCloseReasonToDescription, complaintCloseReasons } from '..'

interface Props {
  complaint: ComplaintDetailState
  messages: ComplaintInternalMessageState
  session: SessionState
}

const ComplaintDetail: React.FC<Props> = ({ complaint, messages, session }) => {
  const dispatch = useDispatch()
  const params = useParams<{ complaintId: string, viewClientInfo: string }>()
  const history = useHistory()
  const { t } = useTranslation(['complaint', 'others'])
  const { theme } = useThemeContext()
  const [showInternalCloseReasonDialog, setShowInternalCloseReasonDialog] = useState<boolean>(false)

  useEffect(() => {
    dispatch(fetchComplaint(params.complaintId))
    dispatch(fetchComplaintInternalMessages(params.complaintId))
  }, [dispatch, params.complaintId, history.location.search])

  const updateStatusComplaintInternalMessage = async (complaintInternalMessage: ComplaintInternalMessage | undefined, complaint: Complaint, updateStatusComplaint: boolean = false) => {
    try {
      if (complaintInternalMessage !== undefined) {
        complaintInternalMessage.status = complaintInternalMessage.status === 'U' ? 'R' : 'U'
        await dispatch(upsertComplaintInternalMessage(complaintInternalMessage))
        await dispatch(fetchComplaintInternalMessages(params.complaintId))
      }
    }
    catch {
    }
  }


  interface InternalCloseReasonProps {
    onConfirm: (reason: string) => void
    onCancel: () => void
    reason: string
  }

  const InternalCloseReasonDialog: React.FC<InternalCloseReasonProps> = (props) => {
    const [reason, setReason] = useState<string>(props.reason)
    const { t } = useTranslation(['complaint', 'menu'])

    const changeReason = (newReason: string) => {
      setReason(newReason)
    }

    const translateCloseReasonDescription = (value: string): string => t(complaintCloseReasonToDescription(value))

    return <GenericModalDialog isOpen={true}
      dialogProps={{ open: true, maxWidth: "lg" }}
      onConfirm={() => props.onConfirm && props.onConfirm(reason)}
      onCancel={() => props.onCancel && props.onCancel()} title={t('internalCloseReason')}>
      <Stack textAlign="center">
        <Box style={{ justifyContent: 'center' }}>
          <Selector
            label={t("internalCloseReason")}
            style={{ marginLeft: "10px", width: '350px', flexGrow: 1 }}
            value={reason}
            required
            key="internalCloseReason"
            menuItems={complaintCloseReasons} // generalDescList()}
            textAdapter={translateCloseReasonDescription}
            onChange={event => changeReason(event.target.value)}
          />
        </Box>
      </Stack>
    </GenericModalDialog>
  }





  const HeaderDetail: React.FC = () => {
    const [downloading, setDownloading] = useState<string>('')

    const downloadFileComplaintInternalMessage = async (complaint: Complaint, fileId: string, fileName: string) => {
      if (complaint.fileId_1 && complaint.id) {
        try {
          setDownloading(fileId)
          await dispatch(downloadComplaintInternalMessageFile(complaint.id, fileId, undefined,
            (file: any) => {

              const url = URL.createObjectURL(new Blob([file], { type: 'application/pdf' }))
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute(
                'download',
                fileName ? fileName : `${complaint.message}.pdf`
              );
              document.body.appendChild(link);
              // Start download
              link.click();

              URL.revokeObjectURL(url);
            }))
          setDownloading('')
        }
        catch {
        }
      }
    }


    return (
      <Paper elevation={2} sx={{ margin: "10px", padding: "2px 2px 2px 2px", bgcolor: theme.palette.success.main }}>
        <Paper elevation={2}
          style={{
            padding: "15px 15px 15px 15px", display: "flex", flexDirection: "column",
            textAlign: "justify", justifyContent: "space-around",
            borderRadius: "4px", margin: "4px"
          }}>
          {(complaint.complaint?.receiptType_1 || complaint.complaint?.receiptType_2 || complaint.complaint?.receiptType_3 ||
            complaint.complaint?.fileId_1 || complaint.complaint?.fileId_2 || complaint.complaint?.fileId_3) &&
            <Box display='flex' flexDirection="row" justifyContent="space-between" alignItems="flex-start">
              <Box display='flex' flexDirection="row" justifyContent="flex-start" alignItems="flex-start">
                {complaint.complaint &&
                  <Box display="flex" flexDirection="column" alignItems="flex-start" >
                    <Typography fontWeight="bold" variant="h6">{t('id')}:&nbsp;&nbsp;</Typography>
                    <Typography fontWeight="bold" variant="h6">{t('dateOpen')}:&nbsp;&nbsp;</Typography>
                    {complaint.complaint.internalAssignedToId > 0 && <Typography fontWeight="bold" variant="h6">{t('internalAssignedToName')}:&nbsp;&nbsp;</Typography>}
                    <Typography fontWeight="bold" variant="h6">{t('category')}:&nbsp;&nbsp;</Typography>
                    <Typography fontWeight="bold" variant="h6">{t('type')}:&nbsp;&nbsp;</Typography>
                    {complaint.complaint.internalDateClose && <Typography fontWeight="bold" variant="h6">{t('internalCloseReason')}:&nbsp;&nbsp;</Typography>}
                  </Box>}

                {complaint.complaint &&
                  <Box display="flex" flexDirection="column" alignItems="flex-start" >
                    <Typography variant="h6">{complaint.complaint.id}</Typography>
                    <Typography variant="h6">{moment(complaint.complaint.dateOpen).tz("America/Argentina/Buenos_Aires").format('DD/MM/YYYY')}</Typography>
                    {complaint.complaint.internalAssignedToId > 0 && <Typography variant="h6">{complaint.complaint.internalAssignedToName}</Typography>}
                    <Typography variant="h6">{complaint.complaint.category}</Typography>
                    <Typography variant="h6">{complaint.complaint.type}</Typography>
                    <Typography variant="h6">{complaint.complaint.internalDateClose ? `${t('closedOn')} ${moment(complaint.complaint.internalDateClose).tz("America/Argentina/Buenos_Aires").format('DD/MM/YYYY')}` : t('active')}</Typography>
                    {complaint.complaint.internalDateClose && complaint.complaint.internalCloseReason && <Typography variant="h6">{t(complaintCloseReasonToDescription(complaint.complaint.internalCloseReason))}</Typography>}
                  </Box>
                }
              </Box>
              {(complaint.complaint?.fileId_1 || complaint.complaint?.fileId_2 || complaint.complaint?.fileId_3) &&
                <Box display='flex' flexDirection="row" justifyContent="flex-end" alignItems="center">
                  <Box display='flex' flexDirection="column" justifyContent="flex-end" alignItems="center">
                    <Table style={{ whiteSpace: "nowrap", overflow: "hidden" }} size="small" >
                      <TableHead>
                        <TableRow selected={true}>
                          <ISTableHeaderCell>{t("files")}</ISTableHeaderCell>
                          <ISTableHeaderCell></ISTableHeaderCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {complaint.complaint?.fileId_1 &&
                          <ISTableRow key={1} color={'default'}>
                            <TableCell>
                              <Typography variant="body1">{complaint.complaint?.fileName_1}</Typography>
                            </TableCell>
                            <TableCell>
                              <IconButton color="secondary"
                                onClick={(e) => { e.stopPropagation(); complaint.complaint?.fileId_1 && complaint.complaint?.fileName_1 && downloadFileComplaintInternalMessage(complaint.complaint, complaint.complaint?.fileId_1, complaint.complaint?.fileName_1) }}>
                                {downloading === complaint.complaint?.fileId_1 ? <CircularProgress size={15} /> : <AttachFile fontSize="small" />}
                              </IconButton>
                            </TableCell>
                          </ISTableRow>
                        }
                        {complaint.complaint?.fileId_2 &&
                          <ISTableRow key={2} color={'default'}>
                            <TableCell>
                              <Typography variant="body1">{complaint.complaint?.fileName_2}</Typography>
                            </TableCell>
                            <TableCell>
                              <IconButton color="secondary"
                                onClick={(e) => { e.stopPropagation(); complaint.complaint?.fileId_2 && complaint.complaint?.fileName_2 && downloadFileComplaintInternalMessage(complaint.complaint, complaint.complaint?.fileId_2, complaint.complaint?.fileName_2) }}>
                                {downloading === complaint.complaint?.fileId_2 ? <CircularProgress size={15} /> : <AttachFile fontSize="small" />}
                              </IconButton>
                            </TableCell>
                          </ISTableRow>
                        }
                        {complaint.complaint?.fileId_3 &&
                          <ISTableRow key={3} color={'default'}>
                            <TableCell>
                              <Typography variant="body1">{complaint.complaint?.fileName_3}</Typography>
                            </TableCell>
                            <TableCell>
                              <IconButton color="secondary"
                                onClick={(e) => { e.stopPropagation(); complaint.complaint?.fileId_3 && complaint.complaint?.fileName_3 && downloadFileComplaintInternalMessage(complaint.complaint, complaint.complaint?.fileId_3, complaint.complaint?.fileName_3) }}>
                                {downloading === complaint.complaint?.fileId_3 ? <CircularProgress size={15} /> : <AttachFile fontSize="small" />}
                              </IconButton>
                            </TableCell>
                          </ISTableRow>
                        }
                      </TableBody>
                    </Table>
                  </Box>
                </Box>}

              <Box display="flex" alignItems="center">
                <Table style={{ whiteSpace: "nowrap", overflow: "hidden" }} size="small" >
                  <TableHead>
                    <TableRow selected={true}>
                      <ISTableHeaderCell>{t("receipt")}</ISTableHeaderCell>
                      <ISTableHeaderCell>{t('receiptId')}</ISTableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>

                    {complaint.complaint?.receiptType_1 &&
                      <ISTableRow key={1} color={'default'}>
                        <TableCell><Typography variant="body1">{t(receiptComplaintTypeToDescription(complaint.complaint?.receiptType_1))}&nbsp;&nbsp;</Typography></TableCell>
                        <TableCell><Typography variant="body1">{complaint.complaint.receiptId_1}</Typography></TableCell>
                      </ISTableRow>
                    }
                    {complaint.complaint?.receiptType_2 &&
                      <ISTableRow key={2} color={'default'}>
                        <TableCell><Typography variant="body1">{t(receiptComplaintTypeToDescription(complaint.complaint?.receiptType_2))}&nbsp;&nbsp;</Typography></TableCell>
                        <TableCell><Typography variant="body1">{complaint.complaint.receiptId_2}</Typography></TableCell>
                      </ISTableRow>
                    }
                    {complaint.complaint?.receiptType_3 &&
                      <ISTableRow key={3} color={'default'}>
                        <TableCell><Typography variant="body1">{t(receiptComplaintTypeToDescription(complaint.complaint?.receiptType_3))}&nbsp;&nbsp;</Typography></TableCell>
                        <TableCell><Typography variant="body1">{complaint.complaint.receiptId_3}</Typography></TableCell>
                      </ISTableRow>
                    }
                  </TableBody>
                </Table>
              </Box>
            </Box>}

          {complaint.complaint &&
            <Box display="flex" flexDirection='row' >
              <Typography fontWeight="bold" variant="h6">{t('complaint')}:&nbsp;&nbsp;</Typography>
              <Typography style={{ wordBreak: "break-word", overflow: 'hidden', textOverflow: 'ellipsis' }} variant="h6">{complaint.complaint.message}</Typography>
            </Box>}
        </Paper>
      </Paper>
    )
  }

  const linkToComplaintInternalAssign = (complaint: Complaint) => {
    const id = complaint.id!.toString()
    if (id && (complaint.internalAssignedToId === null || complaint.internalAssignedToId === undefined))
      dispatch(upsertComplaint({ ...complaint, internalAssignedToId: -1, internalDateOpen: moment().tz("America/Argentina/Buenos_Aires").format() }, () => dispatch(fetchComplaint(id))))
  }

  const linkToComplaintInternalCloseReason = (complaint: Complaint, reason: string) => {
    const id = complaint.id!.toString()
    if (id && (complaint.internalCloseReason === null || complaint.internalCloseReason === undefined))
      dispatch(upsertComplaint({ ...complaint, internalCloseReason: reason, internalDateClose: moment().tz("America/Argentina/Buenos_Aires").format() }, () => dispatch(fetchComplaint(id))))
  }

  return (
    <div>
      <HeaderDetail />
      {!messages.isFetching && <ComplaintInternalMessages
        rows={(messages.complaintInternalMessage || []).map(s => ({ data: s }))}
        loading={messages.isFetching}
        complaint={complaint.complaint!}
        pagination={messages.pagination || undefined}
        owner={session.personId || undefined}
        onClickChangeStatus={updateStatusComplaintInternalMessage}
        viewClientInfo={params.viewClientInfo !== undefined}
        id={params.complaintId}
      />}
      {showInternalCloseReasonDialog && complaint && complaint.complaint && <InternalCloseReasonDialog
          onCancel={() => setShowInternalCloseReasonDialog(false)}
          reason={complaint.complaint.internalCloseReason!}
          onConfirm={(reason) => { linkToComplaintInternalCloseReason(complaint.complaint!, reason); setShowInternalCloseReasonDialog(false) }}
        />}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Button color="secondary" variant="contained" sx={{ textTransform: "none", margin: "10px" }} startIcon={<KeyboardReturnOutlined />} onClick={() => history.goBack()}>{t("back", { ns: 'others' })}</Button>
        {complaint && complaint.complaint && !isClient(session) && !params.viewClientInfo && !complaint.complaint.internalAssignedToId &&
          <Button color="success" variant="contained" sx={{ textTransform: "none", margin: "10px" }}
            startIcon={<AssignmentIndOutlined />}
            onClick={(e) => { e.stopPropagation(); linkToComplaintInternalAssign(complaint.complaint!) }}>
            {t("assignToArea")}
          </Button>}
        
        {complaint && complaint.complaint && (isHeadSales(session) || isStaff(session)) && !params.viewClientInfo &&
          complaint.complaint.internalAssignedToId &&
          <Button color="success" variant="contained" sx={{ textTransform: "none", margin: "10px" }}
            startIcon={complaint.complaint?.internalAssignedToId === -1 ? <HourglassBottomOutlined/> : <FlakyOutlined /> }
            disabled={(complaint.complaint.internalCloseReason !== undefined && complaint.complaint.internalCloseReason !== null) || complaint.complaint.internalAssignedToId === -1 || isHeadSales(session)}
            onClick={(e) => { e.stopPropagation(); setShowInternalCloseReasonDialog(true) }}>
            {complaint.complaint?.internalAssignedToId === -1 ? `${t('waitingInternalAssignedTo')}` : `${t("internalCloseReason")}${complaint.complaint.internalCloseReason ? ': ' + t(complaintCloseReasonToDescription(complaint.complaint.internalCloseReason)) : ''}`}
        </Button>}
      </Box>




      {messages.isFetching &&
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={25} />
        </div>
      }
    </div>
  )
}

const mapStateToProps = (state: AppState) => ({
  complaint: state.complaintDetail,
  messages: state.complaintInternalMessage,
  session: state.session
})


export default connect<Props, {}, {}, AppState>(mapStateToProps, {})(ComplaintDetail);